import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition} from '@angular/cdk/overlay';
import {Subscription} from 'rxjs';
import {UUID} from '../../models/uuid';
import { Team } from 'src/moti/models/team';
import { IconType } from 'src/moti/models/moti-icon';
import { MotiOption, MotiOptionGroup } from 'src/moti/models/moti-option';
import {TeamUsersComponent} from '../team-users/team-users.component';
import {RouterLink} from '@angular/router';
import {NgClass} from '@angular/common';
import { ClickService } from 'src/core/services/click.service';
import {UserAvatarComponent} from '../../../shared/components/user-avatar/user-avatar.component';


@Component({
  selector: 'moti-popover-menu',
  templateUrl: './popover-menu.component.html',
  styleUrls: ['./popover-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TeamUsersComponent,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    RouterLink,
    NgClass,
    UserAvatarComponent,
  ],
})
export class PopoverMenuComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() menuTitle: string;
  @Input() optionGroup: MotiOptionGroup;
  @Input() overlayPositions: ConnectedPosition[] = [{
    originX: 'start',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'top',
  }];
  @Input() iconOnRight: boolean = false;

  options: MotiOption[] = [];

  @Output() clickedOption$: EventEmitter<MotiOption> = new EventEmitter<MotiOption>();
  @Output() clickedOutsideMenu$: EventEmitter<null> = new EventEmitter<null>();
  @Output() clickedAnywhere$: EventEmitter<MotiOption | null> = new EventEmitter<MotiOption | null>();

  clickSub: Subscription;

  @ViewChild('menuOverlay', {read: ElementRef}) private menuEl: ElementRef<HTMLElement>;

  /**
   * Only gets set if needed for displaying team names. The parent component must supply it.
   */
  @Input() teams: Team[];


  constructor(
    private eRef: ElementRef,
    private clickSvc: ClickService,
  ) {
  }


  ngOnInit(): void {
    this.options = this.optionGroup.options;
  }


  ngAfterViewInit() {
    const preselected = this.options.find(o => o.selected);

    if (preselected) {
      const selector: string = `[data-id="${preselected.id}"]`;
      const parentEl: HTMLElement = this.menuEl.nativeElement;
      const thisEl: HTMLAnchorElement | null = parentEl.querySelector(selector);

      if (thisEl) {
        // This places the vertical center of the selected menu option
        // either at the very top of the scrollable area (i.e. making
        // the top half of the option cut off) or lower (i.e. less than
        // half of the option is cut off at the top or the option appears
        // entirely within the current viewable area of the scrollable
        // area).
        thisEl.scrollIntoView({
          block: 'center',
        });

        setTimeout(() => {
          const selectedOptionOffsetTop = thisEl.offsetTop;
          const selectedOptionHeight = thisEl.scrollHeight;
          const visibleMenuHeight = parentEl.clientHeight;
          const totalMenuHeight = parentEl.scrollHeight;

          let scrollByPx = (totalMenuHeight - selectedOptionOffsetTop) - (visibleMenuHeight / 2) - (selectedOptionHeight / 2);
          if (scrollByPx > visibleMenuHeight / 2) {
            scrollByPx = visibleMenuHeight / 2;
          }

          if (scrollByPx > 0) {
            parentEl.scrollBy({
              top: scrollByPx * -1,
            });
          }
        }, 0);
      }
    }

    this.clickSub = this.clickSvc.click$.subscribe(
      ($el) => {
        if (!this.eRef.nativeElement.contains($el)) {
          this.clickedOutsideMenu$.emit(null);
          this.clickedAnywhere$.emit(null);
        }
      },
    );
  }


  ngOnDestroy() {
    this.clickSub?.unsubscribe();
  }


  onClick(option: MotiOption) {
    if (option.callback) {
      option.callback();
    }

    if (this.optionGroup.getSelected()) {
      this.optionGroup.changeSelected(option.id, false);
    }

    this.clickedOption$.emit(option);
    this.clickedAnywhere$.emit(option);
  }


  getTeamName(teamId: UUID): string {
    return this.teams.find(t => t.id === teamId)?.name;
  }


  protected readonly IconType = IconType;
}
